import React, { useState } from 'react';
import optimaLogo from '../assets/images/Optima_LIMS_White_Logo_Transparent.png';
import { Link, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    FileEdit,
    ClipboardList,
    TestTube,
    FileText,
    Package,
    BookOpen,
    Settings,
    ChevronRight,
    LogOut,
} from 'lucide-react';

const ICON_MAP = {
    'uil-home-alt': LayoutDashboard,
    'dripicons-user-group': Users,
    'dripicons-document-edit': FileEdit,
    'dripicons-view-list': ClipboardList,
    'uil-flask': TestTube,
    'dripicons-article': FileText,
    'dripicons-box': Package,
    'uil-file-check': BookOpen,
    'uil-cog': Settings,
};

function NavIcon({ iconClass }) {
    const Icon = ICON_MAP[iconClass] || LayoutDashboard;
    return <Icon size={16} />;
}

function NavItemWithChildren({ item, currentPath, depth = 0 }) {
    const [open, setOpen] = useState(
        item.children.some((c) => currentPath.startsWith(c.url || ''))
    );

    const isAnyChildActive = item.children.some((c) => currentPath === c.url || currentPath.startsWith(c.url || ''));

    return (
        <>
            <button
                className={`lims-sb-item${isAnyChildActive ? ' active' : ''}`}
                onClick={() => setOpen((o) => !o)}
                type="button">
                <span className="lims-sb-icon">
                    <NavIcon iconClass={item.icon} />
                </span>
                <span className="lims-sb-label">{item.label}</span>
                {item.badge && (
                    <span className="lims-sb-badge">{item.badge.text}</span>
                )}
                <span className={`lims-sb-chevron${open ? ' open' : ''}`}>
                    <ChevronRight size={12} />
                </span>
            </button>
            {open && (
                <ul className="lims-sb-children">
                    {item.children.map((child, i) => (
                        <li key={i}>
                            <Link
                                to={child.url}
                                className={`lims-sb-child-item${currentPath === child.url ? ' active' : ''}`}>
                                {child.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
}

function getInitials(firstName, lastName) {
    const f = (firstName || '').trim();
    const l = (lastName || '').trim();
    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
    if (f) return f.slice(0, 2).toUpperCase();
    return 'U';
}

function AppSidebar({ menuItems, user, isOpen, onClose }) {
    const location = useLocation();
    const currentPath = location.pathname;

    const flatItems = menuItems.filter((item) => !item.children);
    const groupItems = menuItems.filter((item) => item.children && item.children.length > 0);

    const appName = 'Optima LIMS';
    const orgName = 'BuoyantWorx';

    return (
        <>
            {/* Mobile overlay */}
            <div
                className={`lims-sb-overlay${isOpen ? ' open' : ''}`}
                onClick={onClose}
            />

            <aside className={`lims-sb${isOpen ? ' open' : ''}`}>
                {/* Logo */}
                <div className="lims-sb-logo">
                    <img src={optimaLogo} alt="Optima LIMS" style={{ height: '28px', display: 'block' }} />
                </div>

                {/* Nav */}
                <nav className="lims-sb-nav">
                    {flatItems.length > 0 && (
                        <div className="lims-sb-section">
                            {flatItems.map((item, i) => (
                                <Link
                                    key={i}
                                    to={item.url}
                                    className={`lims-sb-item${currentPath === item.url ? ' active' : ''}`}
                                    onClick={onClose}>
                                    <span className="lims-sb-icon">
                                        <NavIcon iconClass={item.icon} />
                                    </span>
                                    <span className="lims-sb-label">{item.label}</span>
                                    {item.badge && (
                                        <span className="lims-sb-badge">{item.badge.text}</span>
                                    )}
                                </Link>
                            ))}
                        </div>
                    )}

                    {groupItems.length > 0 && (
                        <>
                            <div className="lims-sb-divider" />
                            <div className="lims-sb-section">
                                {groupItems.map((item, i) => (
                                    <React.Fragment key={i}>
                                        {i > 0 && <div className="lims-sb-group-sep" />}
                                        <NavItemWithChildren
                                            item={item}
                                            currentPath={currentPath}
                                        />
                                    </React.Fragment>
                                ))}
                            </div>
                        </>
                    )}
                </nav>

                {/* User */}
                <div className="lims-sb-bottom">
                    <Link to="/account/logout" className="lims-sb-item lims-sb-logout" style={{ color: '#F09595' }} onClick={onClose}>
                        <span className="lims-sb-icon" style={{ color: '#F09595' }}>
                            <LogOut size={16} color="#F09595" />
                        </span>
                        <span className="lims-sb-label" style={{ color: '#F09595' }}>Logout</span>
                    </Link>
                    <div className="lims-sb-divider" />
                    <div className="lims-sb-user">
                        <div className="lims-sb-avatar">
                            {getInitials(user?.first_name, user?.last_name)}
                        </div>
                        <div>
                            <div className="lims-sb-user-name">
                                {[user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'User'}
                            </div>
                            <div className="lims-sb-user-role">{user?.role || ''}</div>
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
}

export default AppSidebar;
